import React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Trash2 } from "lucide-react";

const TYPE_CONFIG = {
  validation:     { bg: 'rgba(127,199,249,0.20)', color: '#377AD6' },
  transformation: { bg: 'rgba(167,139,250,0.20)', color: 'rgba(109,40,217,0.85)' },
  normalization:  { bg: 'rgba(209,238,176,0.50)', color: '#4DB300' },
  enrichment:     { bg: 'rgba(233,171,48,0.20)',  color: 'rgba(180,120,0,0.85)' },
};

const LANG_CONFIG = {
  javascript:    { bg: 'rgba(233,171,48,0.20)',  color: 'rgba(180,120,0,0.85)' },
  python:        { bg: 'rgba(127,199,249,0.20)', color: '#377AD6' },
  sql:           { bg: 'rgba(6,182,212,0.15)',   color: 'rgba(6,182,212,0.90)' },
  tiva_endpoint: { bg: 'rgba(167,139,250,0.20)', color: 'rgba(109,40,217,0.85)' },
};

const STATUS_CONFIG = {
  Active:     { bg: 'rgba(209,238,176,0.50)', color: '#4DB300' },
  Draft:      { bg: 'rgba(233,171,48,0.20)',  color: 'rgba(233,171,48,0.90)' },
  Deprecated: { bg: 'rgba(222,111,107,0.20)', color: 'rgba(222,111,107,0.90)' },
};

function Pill({ bg, color, label }) {
  return (
    <span className="inline-flex items-center gap-1 h-[18px] rounded-[9px] pl-1 pr-2" style={{ background: bg }}>
      <span className="size-[10px] rounded-full shrink-0" style={{ background: color }} />
      <span className="text-xs font-medium whitespace-nowrap leading-none" style={{ color }}>{label}</span>
    </span>
  );
}

function TypePill({ type }) {
  const cfg = TYPE_CONFIG[type] || { bg: 'rgba(162,178,201,0.20)', color: 'rgba(150,169,183,1)' };
  return (
    <span className="inline-flex items-center h-[18px] rounded-[9px] px-2 text-xs font-medium whitespace-nowrap"
      style={{ background: cfg.bg, color: cfg.color }}>
      {type ? type.charAt(0).toUpperCase() + type.slice(1) : '—'}
    </span>
  );
}

function LangPill({ lang }) {
  const cfg = LANG_CONFIG[lang] || { bg: 'rgba(162,178,201,0.20)', color: 'rgba(150,169,183,1)' };
  const label = lang === 'tiva_endpoint' ? 'Tiva' : lang;
  return (
    <span className="inline-flex items-center h-[18px] rounded-[9px] px-2 text-xs font-medium whitespace-nowrap"
      style={{ background: cfg.bg, color: cfg.color }}>
      {label || '—'}
    </span>
  );
}

const COL = {
  name:    'flex-1 min-w-0',
  type:    'w-[130px] shrink-0',
  lang:    'w-[110px] shrink-0',
  status:  'w-[130px] shrink-0',
  usedBy:  'w-[110px] shrink-0',
  actions: 'w-[60px]  shrink-0',
};

export default function ProcessingRulesTable({ rules, isLoading, onDeleteRule, onRowClick }) {
  if (!isLoading && rules.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-2">
        <p className="text-sm text-page-muted">No processing rules found</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-w-[900px]">
      {/* Header */}
      <div
        className="flex items-center h-9 px-5 gap-[10px] border border-table-header"
        style={{ background: 'var(--table-header-bg)', borderRadius: '8px 8px 0 0' }}
      >
        <div className={`${COL.name} text-sm`} style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Rule name</div>
        <div className={`${COL.type} text-sm`} style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Type</div>
        <div className={`${COL.lang} text-sm`} style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Language</div>
        <div className={`${COL.status} text-sm`} style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Status</div>
        <div className={`${COL.usedBy} text-sm`} style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Used by</div>
        <div className={COL.actions} />
      </div>

      {/* Rows */}
      {isLoading ? (
        Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex items-center h-11 px-5 gap-[10px]"
            style={{ background: i % 2 === 0 ? 'var(--table-stripe)' : 'var(--card-bg)' }}>
            <Skeleton className="h-4 w-48" />
          </div>
        ))
      ) : (
        rules.map((rule, i) => {
          const usageCount = parseInt(rule.usage_count, 10) || 0;
          const statusCfg = STATUS_CONFIG[rule.status] || { bg: 'rgba(162,178,201,0.20)', color: 'rgba(150,169,183,1)' };
          const rowBg = i % 2 === 0 ? 'var(--table-stripe)' : 'var(--card-bg)';
          return (
            <div
              key={rule.id}
              className="group flex items-center h-11 px-5 gap-[10px] cursor-pointer"
              style={{ background: rowBg }}
              onClick={() => onRowClick?.(rule)}
            >
              <div className={`${COL.name} min-w-0`}>
                <div className="text-sm font-medium truncate text-page-text group-hover:text-nav-active transition-colors">{rule.rule_name}</div>
                {rule.description && (
                  <div className="text-xs font-normal truncate" style={{ color: 'var(--page-muted)' }}>{rule.description}</div>
                )}
              </div>
              <div className={COL.type}>
                <TypePill type={rule.rule_type} />
              </div>
              <div className={COL.lang}>
                <LangPill lang={rule.rule_language} />
              </div>
              <div className={COL.status}>
                <Pill bg={statusCfg.bg} color={statusCfg.color} label={rule.status || '—'} />
              </div>
              <div className={COL.usedBy}>
                <span className="text-sm font-normal" style={{ color: 'var(--page-text)' }}>
                  {usageCount} element{usageCount !== 1 ? 's' : ''}
                </span>
              </div>
              <div className={`${COL.actions} flex justify-end`} onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => onDeleteRule(rule)}
                  title="Delete rule"
                  disabled={usageCount > 0}
                  className="flex items-center justify-center bg-transparent border-0 cursor-pointer text-nav-hover hover:text-nav-active transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
